import {
	faExclamationTriangle,
	faEye,
	faPencilAlt,
	faPlus,
	faSpinner,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import SidePanel from "@/components/Sidepanel";
import { FlowType } from "@/store/flowTypes.store";
import { formatDateWithTime } from "@/utils/dateFormatter";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import AddFlowTarget from "./AddFlowTarget";

interface ViewFlowTragetProps {
	flowType: FlowType;
}

interface FlowTarget {
	id: string;
	name: string;
	status: string;
	credentialSchema: Record<string, unknown>;
	inputSchema: Record<string, unknown>;
	logo: string | null;
	createdAt: string;
	updatedAt: string;
}

const FLOW_TARGETS_QUERY_KEY = (flowTypeId: string) =>
	["flow-targets", flowTypeId] as const;

const ViewFlowTraget = ({ flowType }: ViewFlowTragetProps) => {
	const { success } = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [isCreateTargetPanelOpen, setIsCreateTargetPanelOpen] = useState(false);
	const [selectedTarget, setSelectedTarget] = useState<FlowTarget | null>(null);
	const [itemToDelete, setItemToDelete] = useState<{
		id: string;
		type: "target";
		name: string;
	} | null>(null);
	const [isEditTargetPanelOpen, setIsEditTargetPanelOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	// Fetch service providers using TanStack Query
	const {
		data: flowTargets = [],
		isLoading: isLoadingTargets,
		isError,
		error
	} = useQuery({
		queryKey: FLOW_TARGETS_QUERY_KEY(flowType.id),
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_TARGETS", "LIST"], flowType.id)
			);
			return data as FlowTarget[];
		}
	});

	// Delete target mutation
	const deleteTargetMutation = useMutation({
		mutationFn: async (targetId: string) => {
			await apiClient.delete(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_TARGETS", "DELETE_BY_ID"],
					flowType.id,
					targetId
				)
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: FLOW_TARGETS_QUERY_KEY(flowType.id)
			});
			success("Service provider deleted successfully");
			setIsDeleteModalOpen(false);
			setItemToDelete(null);
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to delete service provider");
		}
	});

	// Get target by ID mutation for editing
	const getTargetByIdMutation = useMutation({
		mutationFn: async (targetId: string) => {
			const { data } = await apiClient.get(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_TARGETS", "GET_BY_ID"],
					flowType.id,
					targetId
				)
			);
			return data as FlowTarget;
		},
		onSuccess: (data) => {
			setSelectedTarget(data);
			setIsEditTargetPanelOpen(true);
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to fetch target details");
		}
	});

	const handleDeleteClick = (id: string, type: "target", name: string) => {
		setItemToDelete({ id, type, name });
		setIsDeleteModalOpen(true);
	};

	const handleDelete = () => {
		if (!itemToDelete) return;
		deleteTargetMutation.mutate(itemToDelete.id);
	};

	const handleEditTarget = (targetId: string) => {
		getTargetByIdMutation.mutate(targetId);
	};

	const handleViewTarget = (targetId: string) => {
		navigate(`/admin/integrations/${flowType.id}/${targetId}`);
	};

	if (isLoadingTargets) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	if (isError) {
		handleApiError(error as ApiError, "Failed to load service providers");
		return (
			<div className="flex items-center justify-center h-screen text-red-600">
				Error: Failed to load service providers
			</div>
		);
	}

	return (
		<div className="flex flex-col">
			<div className="flex-1">
				<div className="space-y-6">
					<div className="bg-white shadow rounded-lg mb-6 border">
						<div>
							<div className="border-b flex items-center justify-between mb-4 p-4 bg-blue-50">
								<h2 className="text-xl font-semibold text-gray-900 text-left">
									Service Provider
								</h2>
								<button
									type="button"
									onClick={() => setIsCreateTargetPanelOpen(true)}
									className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-fynxt hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
								>
									<FontAwesomeIcon icon={faPlus} className="mr-2" />
									Create Service Provider
								</button>
							</div>
							<div className="p-4">
								<div className="bg-white shadow-sm rounded-lg overflow-x-auto overflow-y-auto">
									<table className="min-w-full divide-y divide-gray-200 custom-table">
										<thead className="bg-gray-100 sticky top-0 z-10">
											<tr>
												<th className="px-6 py-3 text-center text-xs font-medium text-gray-900 uppercase tracking-wider">
													Name
												</th>
												<th className="px-6 py-3 text-center text-xs font-medium text-gray-900 uppercase tracking-wider">
													Created At
												</th>
												<th className="px-6 py-3 text-center text-xs font-medium text-gray-900 uppercase tracking-wider">
													Status
												</th>
												<th className="px-6 py-3 text-center text-xs font-medium text-gray-900 uppercase tracking-wider">
													Actions
												</th>
											</tr>
										</thead>
										<tbody className="bg-white divide-y divide-gray-200">
											{flowTargets.length ? (
												flowTargets.map((target) => (
													<tr key={target.id} className="hover:bg-gray-50">
														<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
															{target.name}
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
															{formatDateWithTime(target.createdAt)}
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-center">
															<span
																className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
																	target.status === "ENABLED"
																		? "bg-green-100 text-green-800"
																		: "bg-red-100 text-red-800"
																}`}
															>
																{target.status}
															</span>
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
															<div className="flex justify-center space-x-2">
																<button
																	type="button"
																	onClick={() => handleViewTarget(target.id)}
																	className="text-blue-600 hover:text-blue-900 p-1"
																>
																	<FontAwesomeIcon icon={faEye} />
																</button>

																<button
																	type="button"
																	onClick={() => handleEditTarget(target.id)}
																	className="text-green-600 hover:text-green-900 p-1 ml-[5px]"
																>
																	<FontAwesomeIcon icon={faPencilAlt} />
																</button>
																<button
																	type="button"
																	onClick={() =>
																		handleDeleteClick(
																			target.id,
																			"target",
																			target.name
																		)
																	}
																	className="text-red-600 hover:text-red-900 p-1"
																	title="Delete"
																>
																	<FontAwesomeIcon icon={faTrash} />
																</button>
															</div>
														</td>
													</tr>
												))
											) : (
												<tr>
													<td
														colSpan={3}
														className="px-6 py-6 text-center text-gray-400 text-sm whitespace-nowrap"
													>
														No service providers data found.
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>

					{isDeleteModalOpen && itemToDelete && (
						<div className="fixed inset-0 z-50 overflow-y-auto">
							<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
								<div
									className="fixed inset-0 transition-opacity"
									aria-hidden="true"
								>
									<div className="absolute inset-0 bg-gray-500 bg-opacity-75" />
								</div>

								<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
									<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
										<div className="sm:flex sm:items-start">
											<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
												<FontAwesomeIcon
													icon={faExclamationTriangle}
													className="h-6 w-6 text-red-600"
												/>
											</div>
											<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
												<h3 className="text-lg leading-6 font-medium text-gray-900">
													Delete Service Provider
												</h3>
												<div className="mt-2">
													<p className="text-sm text-gray-500">
														Are you sure you want to delete {itemToDelete.name}?
														This action cannot be undone.
													</p>
												</div>
											</div>
										</div>
									</div>
									<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
										<button
											type="button"
											className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
											onClick={handleDelete}
											disabled={deleteTargetMutation.isPending}
										>
											{deleteTargetMutation.isPending ? (
												<>
													<FontAwesomeIcon
														icon={faSpinner}
														spin
														className="mr-2"
													/>
													Deleting...
												</>
											) : (
												"Delete"
											)}
										</button>
										<button
											type="button"
											className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
											onClick={() => {
												setIsDeleteModalOpen(false);
												setItemToDelete(null);
											}}
											disabled={deleteTargetMutation.isPending}
										>
											Cancel
										</button>
									</div>
								</div>
							</div>
						</div>
					)}

					{isCreateTargetPanelOpen && (
						<SidePanel
							isOpen={isCreateTargetPanelOpen}
							onClose={() => setIsCreateTargetPanelOpen(false)}
							title="Create Service Provider"
							closeOnOutsideClick={false}
						>
							<AddFlowTarget
								flowTypeId={flowType.id}
								onClose={() => setIsCreateTargetPanelOpen(false)}
							/>
						</SidePanel>
					)}

					{isEditTargetPanelOpen && selectedTarget && (
						<SidePanel
							isOpen={isEditTargetPanelOpen}
							onClose={() => {
								setIsEditTargetPanelOpen(false);
								setSelectedTarget(null);
							}}
							title="Edit Service Provider"
							closeOnOutsideClick={false}
						>
							<AddFlowTarget
								flowTypeId={flowType.id}
								target={selectedTarget}
								onClose={() => {
									setIsEditTargetPanelOpen(false);
									setSelectedTarget(null);
								}}
							/>
						</SidePanel>
					)}
				</div>
			</div>
		</div>
	);
};

export default ViewFlowTraget;
